/**
 * Shared layout + form chrome for “New * View” modals.
 * Shell uses white / gray in light theme and warm charcoal (#13120A) in dark.
 */

export const nvBackdropClass =
  'fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm dark:bg-black/70 dark:backdrop-blur-[2px]'

export const nvPanelClass =
  'nv-modal relative w-full max-w-[460px] lg:max-w-4xl h-[90vh] rounded-2xl overflow-hidden border border-gray-200 bg-white flex flex-col shadow-xl dark:border-white/[0.08] dark:bg-[#13120A] dark:shadow-none'

export const nvFooterClass =
  'shrink-0 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 dark:border-white/[0.08] dark:bg-[#13120A]'

/** dizee-tickets / new-view modals: Cancel in footer (text-only) */
export const nvModalFooterCancelButtonClass =
  'px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors dark:text-zinc-400 dark:hover:text-zinc-200'

/**
 * Primary CTA in new-view modal footers (Save Changes, Add Show, etc.).
 * Matches Shows modal — brand orange, not outline.
 */
export const nvModalFooterPrimaryButtonClass =
  'px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 ' +
  'bg-[#FF2300] hover:bg-[#E01F00] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2300]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 ' +
  'dark:focus-visible:ring-offset-[#13120A]'

/** Native &lt;option&gt; dropdown surface */
export const nvSelectOptionClass = 'bg-white dark:bg-[#13120A]'

/**
 * Boxed fields (dizee-tickets–style). Focus pop color is enforced in `index.css`
 * (`.nv-modal .nv-field-box:focus`) so it wins over `dark:` border utilities.
 */
export const nvFieldClass =
  'nv-field-box w-full rounded-lg text-sm px-3 py-2 border bg-white border-gray-200 text-gray-900 placeholder-gray-400 ' +
  'outline-none transition-colors ' +
  'focus:border-gray-400 ' +
  'dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-white dark:placeholder-zinc-600 ' +
  'dark:focus:border-white/[0.2]'

/**
 * Line fields (dizee-tickets DigestInput). `nv-input-line` + `.nv-modal` rules in
 * `index.css` set the orange bottom border on :focus (Tailwind `dark:` was overriding).
 */
export const nvInputUnderlineClass =
  'nv-input-line w-full bg-transparent text-sm py-2 text-gray-900 placeholder-gray-400 ' +
  'border-b border-gray-300 outline-none transition-colors ' +
  'focus:border-[#FF2300] focus:outline-none ' +
  'dark:border-white/[0.12] dark:text-white dark:placeholder-zinc-600 ' +
  'dark:focus:border-[#FF2300]'

/**
 * dizee-tickets style switches: emerald when on, charcoal when off
 * (h-5 w-9 and h-6 w-11 tracks with matching thumb motion).
 */
export const nvSwitchSmTrack = 'relative inline-flex h-5 w-9 items-center rounded-full transition-colors'
export const nvSwitchMdTrack = 'relative inline-flex h-6 w-11 items-center rounded-full transition-colors'
export const nvSwitchOn = 'bg-emerald-500'
export const nvSwitchOnDanger = 'bg-red-500'
export const nvSwitchOff = 'bg-zinc-600 dark:bg-zinc-700'
export const nvSwitchThumbSm =
  'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform'
export const nvSwitchThumbMd =
  'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform'

export const nvReadonlyBoxClass =
  'rounded-lg text-sm px-3 py-2 min-h-[36px] border bg-gray-50 border-gray-200 text-gray-900 dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-white'

export const nvLabelClass =
  'block text-[10px] uppercase tracking-wider mb-1 text-gray-500 dark:text-zinc-500'

export const nvSectionDividerLineClass = 'flex-1 h-px bg-gray-200 dark:bg-white/[0.06]'

export const nvIconButtonClass =
  'p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-colors ' +
  'dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-400 dark:hover:text-white'

export const nvPillButtonClass =
  'flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors ' +
  'dark:text-zinc-300 dark:hover:text-white dark:bg-zinc-800 dark:hover:bg-zinc-700'

/**
 * Secondary action in new-view modals (outline + lifted surface — not the generic `Button` chrome).
 */
export const nvSecondaryCtaClass =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 ' +
  'outline-none transition-colors hover:bg-gray-50 hover:border-gray-300 ' +
  'focus-visible:ring-2 focus-visible:ring-[#FF2300]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-white ' +
  'dark:border-white/[0.12] dark:bg-white/[0.04] dark:text-white dark:hover:bg-white/[0.07] dark:hover:border-white/[0.18] ' +
  'dark:focus-visible:ring-offset-[#13120A]'

/** “Add {entity}” modals: left column (required fields) */
export const nvAddColumnRequiredHeading =
  'text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF2300] mb-4'

/** “Add {entity}” modals: right column (accordions) */
export const nvAddColumnAdditionalHeading =
  'text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-zinc-500 mb-4'

export const nvAddModalSubtitleClass = 'text-sm text-gray-500 dark:text-zinc-500 mt-1'

export const nvRequiredAsteriskClass = 'text-[#FF2300] font-semibold'
